import { format } from 'date-fns';
import type { Customer } from '../types';

export interface PipelineBooking {
  type?: string;
  label?: string;
  staffName?: string | null;
  staffEmail?: string | null;
  startsAt?: string;
  endsAt?: string;
  durationMinutes?: number;
  eventId?: string | null;
  bookedAt?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function optionalNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return optionalString(value);
}

function optionalDateString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : value;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function getPipelineBooking(
  customer: Pick<Customer, 'additionalData'>
): PipelineBooking | null {
  const booking = customer.additionalData?.pipelineBooking;
  if (!isRecord(booking)) return null;

  const startsAt = optionalDateString(booking.startsAt);
  if (!startsAt) return null;

  return {
    type: optionalString(booking.type),
    label: optionalString(booking.label),
    staffName: optionalNullableString(booking.staffName),
    staffEmail: optionalNullableString(booking.staffEmail),
    startsAt,
    endsAt: optionalDateString(booking.endsAt),
    durationMinutes: optionalNumber(booking.durationMinutes),
    eventId: optionalNullableString(booking.eventId),
    bookedAt: optionalDateString(booking.bookedAt),
  };
}

export function getPipelineBookingLabel(booking: PipelineBooking): string {
  if (booking.label) return booking.label;
  if (booking.type === 'site_visit') return 'Site Visit';
  if (booking.type === 'design_call') return 'Design Call';
  return 'Appointment';
}

export function formatPipelineBookingDateTime(booking: PipelineBooking, withYear = false): string {
  if (!booking.startsAt) return '';
  const startsAt = new Date(booking.startsAt);
  if (Number.isNaN(startsAt.getTime())) return '';
  return format(startsAt, withYear ? 'EEE d MMM yyyy, HH:mm' : 'EEE d MMM, HH:mm');
}

export function formatPipelineBookingTime(booking: PipelineBooking): string {
  if (!booking.startsAt) return '';
  const startsAt = new Date(booking.startsAt);
  if (Number.isNaN(startsAt.getTime())) return '';
  return format(startsAt, 'HH:mm');
}
